import React, { useState, useEffect, useRef } from "react";
import { useEducation } from "../../../hooks/useEducation";
import {
  AccessTime,
  Bookmark,
  List,
  PlayArrow,
  LibraryBooks,
} from "@mui/icons-material";
import BookmarkList from "./BookmarkList";
import QuizViewer from "./QuizViewer";
import QuizModal from "./QuizModal";
import "./ContentViewer.css";

const ContentViewer = ({
  content,
  classId,
  bookmarks = [],
  bookmarksLoading = false,
  onBookmarkCreate,
  onBookmarkDelete,
  classProgress,
}) => {
  const [contentProgress, setContentProgress] = useState(null);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showChapters, setShowChapters] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);
  const [currentSubtitle, setCurrentSubtitle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chapters, setChapters] = useState([]);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const videoRef = useRef(null);
  const intervalRef = useRef(null);

  const {
    updateContentPosition,
    markContentCompleted,
    createBookmark,
    deleteBookmark,
    fetchContentProgress,
    createContentProgress,
  } = useEducation();

  // Parse chapters from content metadata
  useEffect(() => {
    if (content?.chapters) {
      setChapters(content.chapters);
    } else {
      setChapters([]);
    }
  }, [content]);

  // Fetch or initialize content progress
  useEffect(() => {
    const initializeContentProgress = async () => {
      if (!content?.id) return;

      setLoading(true);
      try {
        // Try to fetch existing progress
        const progressData = await fetchContentProgress({
          content: content.id,
        });

        if (progressData && progressData.length > 0) {
          // Found existing progress
          setContentProgress(progressData[0]);
        } else {
          const newProgressData = {
            class_progress_id: classProgress,
            content: content.id,
            current_position: 0,
          };

          const newProgress = await createContentProgress(newProgressData);
          setContentProgress(newProgress);
        }
      } catch (err) {
        console.error("Error initializing content progress:", err);
      } finally {
        setLoading(false);
      }
    };

    if (content?.id) {
      initializeContentProgress();
    }
  }, [content?.id, createContentProgress, fetchContentProgress, classProgress]);

  // Handle video load and set initial position
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !content?.content_type_name.toLowerCase().includes("video")) {
      return;
    }

    const handleLoadedData = () => {
      setIsVideoLoaded(true);

      // Set initial position from progress
      if (contentProgress?.current_position) {
        video.currentTime = contentProgress.current_position;
        setCurrentPlaybackTime(contentProgress.current_position);
      }
    };

    const handleTimeUpdate = () => {
      const currentTime = video.currentTime;
      setCurrentPlaybackTime(currentTime);

      // Find current subtitle
      if (content.subtitles && content.subtitles.length > 0) {
        const activeSubtitle = content.subtitles.find(
          (sub) => currentTime >= sub.start_time && currentTime <= sub.end_time
        );
        setCurrentSubtitle(activeSubtitle);
      }
    };

    video.addEventListener("loadeddata", handleLoadedData);
    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [content?.content_type_name, content?.subtitles, contentProgress]);

  // Update backend position periodically
  useEffect(() => {
    if (
      !isVideoLoaded ||
      !contentProgress ||
      !content?.content_type_name.toLowerCase().includes("video")
    ) {
      return;
    }

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;

      const currentTime = video.currentTime;

      // Update backend every 5 seconds if position changed significantly
      if (Math.abs(currentTime - (contentProgress.current_position || 0)) > 5) {
        updateContentPosition(contentProgress.id, currentTime).catch((error) =>
          console.error("Failed to update content position:", error)
        );

        // Update local progress state
        setContentProgress((prev) => ({
          ...prev,
          current_position: currentTime,
        }));
      }
    }, 5000); // Update every 5 seconds

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    isVideoLoaded,
    contentProgress,
    content?.content_type_name,
    updateContentPosition,
  ]);

  const handleVideoEnd = async () => {
    if (contentProgress) {
      try {
        const response = await markContentCompleted(contentProgress.id);

        // Check if the response contains quiz data
        if (response && response.has_quiz && response.quiz_data) {
          setQuizData(response.quiz_data);
          setShowQuizModal(true);
        }
      } catch (error) {
        console.error("Failed to mark content as completed:", error);
      }
    }
  };

  // Handle creating bookmarks (separate from chapters)
  const handleCreateBookmark = async () => {
    if (!contentProgress || !videoRef.current) return;

    const currentTime = videoRef.current.currentTime;

    // Get the closest subtitle if it's a video with subtitles
    let subtitle = null;
    if (content.subtitles && content.subtitles.length > 0) {
      subtitle = content.subtitles.find(
        (sub) => currentTime >= sub.start_time && currentTime <= sub.end_time
      );
    }

    const formattedTime = formatTime(currentTime);
    let bookmarkNote = `Bookmark at ${formattedTime}`;

    // If there's a subtitle at this time, include part of the text
    if (subtitle && subtitle.text) {
      const shortText =
        subtitle.text.length > 50
          ? subtitle.text.substring(0, 50) + "..."
          : subtitle.text;
      bookmarkNote = `${formattedTime}: "${shortText}"`;
    }

    const bookmarkData = {
      edu_class: classId,
      content: content.id,
      subtitle: subtitle?.id,
      timestamp: currentTime,
      note: bookmarkNote,
    };

    try {
      const newBookmark = await createBookmark(bookmarkData);
      // Immediately update parent component's bookmark list
      if (onBookmarkCreate) {
        onBookmarkCreate(newBookmark);
      }
    } catch (error) {
      console.error("Failed to create bookmark:", error);
    }
  };

  // Handle creating bookmark from transcript
  const handleCreateBookmarkFromTranscript = async (subtitle) => {
    const bookmarkData = {
      edu_class: classId,
      content: content.id,
      subtitle: subtitle.id,
      timestamp: subtitle.start_time,
      note: `${formatTime(subtitle.start_time)}: "${
        subtitle.text.length > 50
          ? subtitle.text.substring(0, 50) + "..."
          : subtitle.text
      }"`,
    };

    try {
      const newBookmark = await createBookmark(bookmarkData);
      // Immediately update parent component's bookmark list
      if (onBookmarkCreate) {
        onBookmarkCreate(newBookmark);
      }
    } catch (error) {
      console.error("Failed to create bookmark:", error);
    }
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  // Jump to specific time (used for both chapters and bookmarks)
  const jumpToTime = (timestamp) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timestamp;
      videoRef.current.play().catch(() => {
        // Autoplay prevented by browser
      });
    }
  };

  // Jump to specific subtitle/transcript segment
  const jumpToSubtitle = (subtitle) => {
    if (videoRef.current) {
      videoRef.current.currentTime = subtitle.start_time;
      videoRef.current.play().catch(() => {
        // Autoplay prevented by browser
      });
    }
  };

  // Render transcript/subtitles
  const renderTranscript = () => {
    if (!content.subtitles || content.subtitles.length === 0) {
      return (
        <div className="empty-state">
          No transcript available for this video
        </div>
      );
    }

    return (
      <div className="transcript-list">
        <h3 className="panel-title">
          <List className="icon" />
          Video Transcript
        </h3>
        <div className="transcript-container">
          {content.subtitles.map((subtitle, index) => (
            <div
              key={subtitle.id || index}
              className={`transcript-item ${
                currentSubtitle?.id === subtitle.id ? "active" : ""
              }`}
              onClick={() => jumpToSubtitle(subtitle)}
            >
              <div className="transcript-content">
                <div className="transcript-header">
                  <span className="timestamp">
                    {formatTime(subtitle.start_time)}
                  </span>
                  <button
                    className="bookmark-btn small"
                    onClick={(e) => {
                      e.stopPropagation();
                      jumpToSubtitle(subtitle);
                      handleCreateBookmarkFromTranscript(subtitle);
                    }}
                  >
                    <Bookmark className="small-icon" /> Bookmark
                  </button>
                </div>
                <p className="transcript-text">{subtitle.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle bookmark clicks
  const jumpToBookmark = (timestamp) => {
    jumpToTime(timestamp);
  };

  const renderChapters = () => {
    if (!chapters || chapters.length === 0) {
      return (
        <div className="empty-state">No chapters available for this video</div>
      );
    }

    return (
      <div className="chapters-list">
        <h3 className="panel-title">
          <LibraryBooks className="icon" />
          Video Chapters
        </h3>
        <ul className="chapters-container">
          {chapters.map((chapter, index) => (
            <li
              key={chapter.id || index}
              className="chapter-item"
              onClick={() => jumpToTime(chapter.timestamp)}
            >
              <PlayArrow className="play-icon" />
              <div className="chapter-content">
                <div className="chapter-header">
                  <span className="chapter-title">{chapter.title}</span>
                  <span className="timestamp">
                    {formatTime(chapter.timestamp)}
                  </span>
                </div>
                {chapter.description && (
                  <p className="chapter-description">{chapter.description}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    try {
      await deleteBookmark(bookmarkId);

      if (onBookmarkDelete) {
        onBookmarkDelete(bookmarkId);
      }
    } catch (error) {
      console.error("Failed to delete bookmark:", error);
    }
  };

  // Convert Google Drive URL to direct video URL
  const getVideoUrl = (url) => {
    if (url && url.includes("drive.google.com/file/d/")) {
      // Extract file ID from Google Drive URL
      const match = url.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);

      if (match) {
        const fileId = match[1];
        // Try multiple Google Drive URL formats
        const directUrl = `https://drive.google.com/uc?export=download&id=${fileId}`;
        const streamUrl = `https://drive.google.com/uc?export=stream&id=${fileId}`;
        const viewUrl = `https://drive.google.com/file/d/${fileId}/preview`;

        // Return the direct download URL first
        return directUrl;
      }
    }

    return url;
  };

  const renderVideoWithControls = () => {
    const videoUrl = content.content_url;
    const isGoogleDrive =
      videoUrl && videoUrl.includes("drive.google.com/file/d/");

    if (isGoogleDrive) {
      // Extract file ID for Google Drive
      const match = videoUrl.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
      const fileId = match ? match[1] : null;
      const embedUrl = fileId
        ? `https://drive.google.com/file/d/${fileId}/preview`
        : videoUrl;

      return (
        <div className="video-container">
          <div
            className="video-wrapper"
            style={{ position: "relative", paddingBottom: "56.25%" }}
          >
            <iframe
              src={embedUrl}
              title={content.title || "Google Drive Video"}
              className="video-player"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
              allowFullScreen
            />
          </div>
          <div className="video-controls">
            <div className="controls-left">
              <button
                className="btn btn-primary"
                onClick={handleCreateBookmark}
              >
                <Bookmark className="btn-icon" /> Add Bookmark
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowBookmarks(!showBookmarks)}
              >
                <List className="btn-icon" />
                {showBookmarks ? "Hide Bookmarks" : "Show Bookmarks"}
              </button>

              {chapters.length > 0 && (
                <button
                  className="btn content-btn-success"
                  onClick={() => setShowChapters(!showChapters)}
                >
                  <LibraryBooks className="btn-icon" />
                  {showChapters ? "Hide Chapters" : "Show Chapters"}
                </button>
              )}

              {content.subtitles && content.subtitles.length > 0 && (
                <button
                  className="btn btn-info"
                  onClick={() => setShowTranscript(!showTranscript)}
                >
                  <List className="btn-icon" />
                  {showTranscript ? "Hide Transcript" : "Show Transcript"}
                </button>
              )}
            </div>

            <div className="controls-right" style={{ marginLeft: "5px" }}>
              <AccessTime className="time-icon" />
              <span>Google Drive Video - Use player controls</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="video-container">
        <video
          ref={videoRef}
          src={getVideoUrl(content.content_url)}
          controls
          className="video-player"
          onEnded={handleVideoEnd}
          onError={(e) => {
            // Handle video playback errors silently
          }}
        />

        <div className="video-controls">
          <div className="controls-left">
            <button className="btn btn-primary" onClick={handleCreateBookmark}>
              <Bookmark className="btn-icon" /> Add Bookmark
            </button>

            <button
              className="btn btn-secondary"
              onClick={() => setShowBookmarks(!showBookmarks)}
            >
              <List className="btn-icon" />
              {showBookmarks ? "Hide Bookmarks" : "Show Bookmarks"}
            </button>

            {chapters.length > 0 && (
              <button
                className="btn content-btn-success"
                onClick={() => setShowChapters(!showChapters)}
              >
                <LibraryBooks className="btn-icon" />
                {showChapters ? "Hide Chapters" : "Show Chapters"}
              </button>
            )}

            {content.subtitles && content.subtitles.length > 0 && (
              <button
                className="btn btn-info"
                onClick={() => setShowTranscript(!showTranscript)}
              >
                <List className="btn-icon" />
                {showTranscript ? "Hide Transcript" : "Show Transcript"}
              </button>
            )}
          </div>

          <div className="controls-right">
            <AccessTime className="time-icon" />
            Current: {formatTime(currentPlaybackTime)}
            {currentSubtitle && (
              <span className="current-subtitle">
                Speaking: "
                {currentSubtitle.text.length > 30
                  ? currentSubtitle.text.substring(0, 30) + "..."
                  : currentSubtitle.text}
                "
              </span>
            )}
          </div>
        </div>

        {/* Bookmarks Panel */}
        {showBookmarks && (
          <div className="panel bookmarks-panel">
            <h3 className="panel-title">
              <Bookmark className="icon" />
              Your Bookmarks
            </h3>
            <BookmarkList
              bookmarks={bookmarks.filter((b) => b.content === content.id)}
              loading={bookmarksLoading}
              onBookmarkClick={jumpToBookmark}
              onBookmarkDelete={handleDeleteBookmark}
            />
          </div>
        )}

        {/* Chapters Panel */}
        {showChapters && (
          <div className="panel chapters-panel">{renderChapters()}</div>
        )}

        {/* Transcript Panel */}
        {showTranscript && (
          <div className="panel transcript-panel">{renderTranscript()}</div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (!content) return <div>No content selected</div>;

    const contentTypeLabel = content?.content_type_name.toLowerCase() || "";

    if (contentTypeLabel.includes("quiz")) {
      return (
        <QuizViewer
          content={content}
          classId={classId}
          classProgress={classProgress}
          onQuizCompleted={(results) => {
            // Quiz completed successfully
          }}
        />
      );
    } else if (contentTypeLabel.includes("video")) {
      return renderVideoWithControls();
    } else if (contentTypeLabel.includes("text")) {
      return (
        <div className="text-content">
          <div
            className="text-viewer"
            dangerouslySetInnerHTML={{ __html: content.content_text }}
          />
        </div>
      );
    } else if (contentTypeLabel.includes("slide")) {
      return (
        <div className="slide-container">
          <iframe
            src={content.content_url}
            title={content.title}
            className="slide-frame"
          />
        </div>
      );
    } else {
      return (
        <div className="generic-content">
          <h3>Content: {content.title}</h3>
          {content.content_url && (
            <a
              href={content.content_url}
              target="_blank"
              rel="noopener noreferrer"
              className="content-link"
            >
              Open Content
            </a>
          )}
          {content.content_text && (
            <div className="content-text">{content.content_text}</div>
          )}
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="content-loading">
        <div className="loading-content">
          <div className="spinner"></div>
          Loading content...
        </div>
      </div>
    );
  }

  return (
    <div className="content-viewer">
      <div className="content-display">{renderContent()}</div>
      <div className="content-contents">
        <h2 className="content-title">{content.title}</h2>
        {content.description && (
          <div className="content-description">{content.description}</div>
        )}
      </div>

      {/* Quiz Modal */}
      <QuizModal
        isOpen={showQuizModal}
        onClose={() => setShowQuizModal(false)}
        quizData={quizData}
        contentId={content?.id}
        classProgress={classProgress}
        onQuizCompleted={(results) => {
          // Don't close the modal immediately - let the user see the results first
          // The modal will be closed when the user clicks the "Close" button
          // You can add additional logic here when quiz is completed
        }}
      />
    </div>
  );
};

export default ContentViewer;
